import { STEPSCONFIG } from "@features/Members/AddManually/EnumConfig";
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

export interface AttachmentFileInfo {
	name: string;
	size: number;
	type: string;
}

export interface AddMembersFormState {
	// Step 1: Basic Info
	basicInfo: {
		firstName: string;
		lastName: string;
		emailAddress: string;
		phoneNumber: string;
		technology: number | null;
		dob: string;
		cnic: string;
		designation: number | null;
		qualification: string | number;
		dateOfJoining: string;
		grade: number | null;
		address: string;
		lat: number | null;
		long: number | null;
		organizationType: string | number;
	};

	// Step 2: Current Posting
	currentPosting: {
		province: string | number;
		division: string | number;
		district: string | number;
		tehsil: string | number;
		city: string | number;
		unionCouncil: string | number;
		instituteName: string;
		grade: string | number;
		dateOfJoining: string;
		designation: string | number;
		technologies: (string | number)[];
	};

	// Step 3: Employment Details
	employmentDetails: {
		salary: number | "";
		paymentMode: string;
		employmentType: string;
		experienceYears: number | "";
		remarks: string;
		isActive: boolean;
	};

	// Step 4: Attachments
	attachments: {
		attachment: string;
		cv: string;
		experienceLetter: string[];
		tradeCertificate: string;
		fileInfo: {
			attachment: AttachmentFileInfo | null;
			cv: AttachmentFileInfo | null;
			experienceLetter: AttachmentFileInfo[];
			tradeCertificate: AttachmentFileInfo | null;
		};
	};

	// UI State
	currentStep: number;
	isFormValid: boolean;
}

const initialState: AddMembersFormState = {
	basicInfo: {
		firstName: "",
		lastName: "",
		emailAddress: "",
		phoneNumber: "",
		technology: null,
		designation: null,
		qualification: "",
		dateOfJoining: "",
		dob: "",
		cnic: "",
		grade: null,
		address: "",
		lat: null,
		long: null,
		organizationType: "",
	},
	currentPosting: {
		province: "",
		division: "",
		district: "",
		tehsil: "",
		city: "",
		unionCouncil: "",
		instituteName: "",
		grade: "",
		dateOfJoining: "",
		designation: "",
		technologies: [],
	},
	employmentDetails: {
		salary: "",
		paymentMode: "",
		employmentType: "",
		experienceYears: "",
		remarks: "",
		isActive: true,
	},
	attachments: {
		attachment: "",
		cv: "",
		experienceLetter: [],
		tradeCertificate: "",
		fileInfo: {
			attachment: null,
			cv: null,
			experienceLetter: [],
			tradeCertificate: null,
		},
	},
	currentStep: 0,
	isFormValid: false,
};

const addMembersSlice = createSlice({
	name: "addMembers",
	initialState,
	reducers: {
		updateBasicInfo: (state, action: PayloadAction<Partial<AddMembersFormState["basicInfo"]>>) => {
			state.basicInfo = { ...state.basicInfo, ...action.payload };
		},
		updateCurrentPosting: (state, action: PayloadAction<Partial<AddMembersFormState["currentPosting"]>>) => {
			state.currentPosting = { ...state.currentPosting, ...action.payload };
		},
		updateEmploymentDetails: (state, action: PayloadAction<Partial<AddMembersFormState["employmentDetails"]>>) => {
			state.employmentDetails = { ...state.employmentDetails, ...action.payload };
		},
		updateAttachments: (state, action: PayloadAction<Partial<AddMembersFormState["attachments"]>>) => {
			state.attachments = { ...state.attachments, ...action.payload };
		},

		setCurrentStep: (state, action: PayloadAction<number>) => {
			state.currentStep = action.payload;
		},
		nextStep: state => {
			if (state.currentStep < 3) {
				state.currentStep += 1;
			}
		},
		previousStep: state => {
			if (state.currentStep > 0) {
				state.currentStep -= 1;
			}
		},

		setFormValid: (state, action: PayloadAction<boolean>) => {
			state.isFormValid = action.payload;
		},

		resetForm: () => {
			return initialState;
		},

		resetStep: (state, action: PayloadAction<keyof Omit<AddMembersFormState, "currentStep" | "isFormValid">>) => {
			const stepKey = action.payload;
			if (stepKey === STEPSCONFIG.BASIC_INFO) {
				state.basicInfo = initialState.basicInfo;
			} else if (stepKey === STEPSCONFIG.CURRENT_POSTING) {
				state.currentPosting = initialState.currentPosting;
			} else if (stepKey === STEPSCONFIG.EMPLOYMENT_DETAILS) {
				state.employmentDetails = initialState.employmentDetails;
			} else if (stepKey === STEPSCONFIG.ATTACHEMENTS) {
				state.attachments = initialState.attachments;
			}
		},
	},
});

export const {
	updateBasicInfo,
	updateCurrentPosting,
	updateEmploymentDetails,
	updateAttachments,
	setCurrentStep,
	nextStep,
	previousStep,
	setFormValid,
	resetForm,
	resetStep,
} = addMembersSlice.actions;

export default addMembersSlice.reducer;
